import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import {
  Code,
  Search,
  Share2,
  Blocks,
  ShieldCheck,
  MessageCircle,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    icon: Code,
    title: "Website Development",
    description: "Custom websites built for speed, clarity, and conversion.",
    path: "/services/website-development",
    cta: "Learn More",
  },
  {
    icon: Search,
    title: "SEO Services",
    description:
      "Search engine optimization services that help the right clients find you.",
    path: "/services/seo-services",
    cta: "Learn More",
  },
  {
    icon: Share2,
    title: "Social Media Marketing",
    description:
      "Consistent content and strategy that help your brand show up and build trust.",
    path: "/services/social-media-marketing",
    cta: "Learn More",
  },
  {
    icon: Blocks,
    title: "Custom Tool Creation",
    description:
      "Dashboards, calculators, and custom web applications built around your workflow.",
    path: "/services/custom-tool-creation",
    cta: "Learn More",
  },
  {
    icon: ShieldCheck,
    title: "Maintenance & Support",
    description:
      "Ongoing updates, fixes, and technical support to keep everything performing well.",
    path: "/services/maintenance-support",
    cta: "Learn More",
  },
  {
    icon: MessageCircle,
    title: "Not Sure What You Need?",
    description:
      "Tell us the problem. We’ll help you find the right digital solution.",
    path: "/contact",
    cta: "Contact Us",
  },
];
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

export const ServicesSection = () => {
  return (
    <section className="pt-8 sm:pt-10 lg:pt-16 pb-12 sm:pb-14 lg:pb-16 bg-muted/30 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-16 left-10 sm:top-20 sm:left-20 w-56 h-56 sm:w-72 sm:h-72 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-16 right-10 sm:bottom-20 sm:right-20 w-72 h-72 sm:w-96 sm:h-96 bg-secondary/10 rounded-full blur-3xl" />
      </div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-10 sm:mb-14"
        >
          <span className="text-sm font-medium text-primary uppercase tracking-wider">
            What We Do
          </span>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mt-3 mb-4 sm:mb-6">
            Complete Digital Solutions for{" "}
            <span className="gradient-text">Growing Businesses</span>
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            We help businesses improve visibility, strengthen trust, and solve
            digital problems with practical services built around real goals.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div key={index} variants={itemVariants} className="h-full">
                <Link to={service.path} className="block h-full">
                  <Card className="h-full p-5 sm:p-6 bg-card border-border hover:border-primary/30 transition-all duration-300 card-hover group flex flex-col items-center text-center md:items-start md:text-left">
                    {/* Icon */}
                   <div
  className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl border border-primary/15 bg-primary/10 flex items-center justify-center mb-4 sm:mb-5 group-hover:scale-105 group-hover:bg-primary/15 transition-all duration-300 mx-auto md:mx-0"
>
  <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-primary" />
</div>

                    {/* Content */}
                    <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2 sm:mb-3 group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>

                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed flex-grow mx-auto md:mx-0">
                      {service.description}
                    </p>

                    {/* Link */}
                    <div className="flex items-center justify-center md:justify-start gap-2 text-primary font-medium mt-4 sm:mt-5 group-hover:gap-3 transition-all">
                      <span>{service.cta}</span>
                      <ArrowUpRight className="h-4 w-4" />
                    </div>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};